import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Minus, Plus, Check, Truck, RotateCcw, ChevronDown } from 'lucide-react';
import { products } from '../mock/mock';
import { useCart } from '../context/CartContext';
import Lightbox from '../components/Lightbox';

const Product = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = useMemo(() => products.find((p) => p.id === id), [id]);
  const { addToCart, toggleWishlist, wishlist } = useCart();

  const [selectedSize, setSelectedSize] = useState(product?.sizes?.[0] || '');
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState(0);
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [added, setAdded] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (product) {
      setSelectedSize(product.sizes[0]);
      setQuantity(1);
      setActiveImage(0);
    }
  }, [id, product]);

  if (!product) {
    return (
      <div className="pt-40 pb-32 text-center px-6">
        <h1 className="font-display text-4xl mb-4">Piece not found</h1>
        <Link
          to="/shop"
          className="text-[11px] tracking-[0.22em] uppercase border-b border-neutral-900 pb-1"
        >
          Back to Shop
        </Link>
      </div>
    );
  }

  const isWishlisted = wishlist.includes(product.id);
  const gallery = product.images || [product.image];
  // Treat images after the first front/back as "From Every Angle" extras.
  // Heuristic: any image whose URL contains "-angle-".
  const angleImages = gallery.filter((u) => /(-angle-|\bangle\b)/i.test(u));

  const handleAdd = () => {
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: gallery[0] || product.image,
      size: selectedSize,
      color: product.color || '',
      quantity,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  const related = products
    .filter((p) => p.id !== product.id && p.category === product.category)
    .slice(0, 4);
  const relatedFallback = products.filter((p) => p.id !== product.id).slice(0, 4);
  const relatedList = related.length >= 3 ? related : relatedFallback;

  return (
    <div className="pt-28 pb-32" data-testid="product-page">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12">
        <button
          onClick={() => navigate('/shop')}
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-neutral-700 hover:text-neutral-900 mb-10"
          data-testid="back-to-shop"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to Shop
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-12 lg:gap-20">
          {/* Gallery */}
          <div className="flex flex-col gap-4">
            <div
              className="aspect-[4/5] bg-neutral-100 overflow-hidden cursor-zoom-in"
              onClick={() => setLightboxSrc(gallery[activeImage] || gallery[0])}
              data-testid="main-image-zoom"
            >
              <img
                key={activeImage}
                src={gallery[activeImage] || gallery[0]}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>

            {gallery.length > 1 && (
              <div className="grid grid-cols-4 gap-3" data-testid="gallery-thumbs">
                {gallery.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImage(i)}
                    className={`aspect-[3/4] overflow-hidden bg-neutral-100 relative border ${
                      activeImage === i ? 'border-neutral-900' : 'border-transparent hover:border-neutral-300'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="lg:pt-8 lg:sticky lg:top-28 self-start">
            <p className="text-[11px] tracking-[0.22em] uppercase text-neutral-500 mb-4">
              {product.category}
            </p>
            <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight mb-6">
              {product.name}
            </h1>
            <div className="flex items-center gap-3 mb-8">
              <span className="text-2xl font-display">${product.price}</span>
              {product.originalPrice && (
                <span className="text-lg text-neutral-400 line-through">${product.originalPrice}</span>
              )}
            </div>
            <p className="text-[15px] leading-relaxed text-neutral-600 mb-10">
              {product.description}
            </p>

            {/* Size */}
            <div className="mb-8">
              <p className="text-[11px] tracking-[0.22em] uppercase text-neutral-500 mb-3">Size</p>
              <div className="flex flex-wrap gap-2" data-testid="size-selector">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSelectedSize(s)}
                    data-testid={`size-${s}`}
                    className={`min-w-[52px] px-4 py-3 text-sm border ${
                      selectedSize === s
                        ? 'border-neutral-900 bg-neutral-900 text-white'
                        : 'border-neutral-300 text-neutral-900 hover:border-neutral-900'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity */}
            <div className="mb-8">
              <p className="text-[11px] tracking-[0.22em] uppercase text-neutral-500 mb-3">Quantity</p>
              <div className="inline-flex items-center border border-neutral-300">
                <button
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  className="w-11 h-11 flex items-center justify-center hover:bg-neutral-100"
                  aria-label="Decrease"
                >
                  <Minus className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
                <span className="w-12 text-center text-sm">{quantity}</span>
                <button
                  onClick={() => setQuantity((q) => q + 1)}
                  className="w-11 h-11 flex items-center justify-center hover:bg-neutral-100"
                  aria-label="Increase"
                >
                  <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 mb-10">
              <button
                onClick={handleAdd}
                data-testid="add-to-bag"
                className="flex-1 bg-neutral-900 text-white py-4 text-[11px] tracking-[0.22em] uppercase hover:bg-neutral-700 flex items-center justify-center gap-2"
              >
                {added ? (
                  <>
                    <Check className="w-4 h-4" strokeWidth={1.5} /> Added
                  </>
                ) : (
                  'Add to Bag'
                )}
              </button>
              <button
                onClick={() => toggleWishlist(product.id)}
                aria-label="Add to wishlist"
                className={`w-14 h-14 border flex items-center justify-center ${
                  isWishlisted
                    ? 'border-neutral-900 bg-neutral-900 text-white'
                    : 'border-neutral-300 hover:border-neutral-900'
                }`}
              >
                <Heart
                  className="w-4 h-4"
                  strokeWidth={1.5}
                  fill={isWishlisted ? 'currentColor' : 'none'}
                />
              </button>
            </div>

            {/* Details accordion */}
            <div className="border-t border-neutral-200">
              <button
                onClick={() => setDetailsOpen((o) => !o)}
                className="w-full flex items-center justify-between py-5 text-[11px] tracking-[0.22em] uppercase"
              >
                Details &amp; Care
                <ChevronDown
                  className={`w-4 h-4 ${detailsOpen ? 'rotate-180' : ''}`}
                  strokeWidth={1.5}
                />
              </button>
              {detailsOpen && (
                <p className="text-sm text-neutral-600 leading-relaxed pb-5">{product.details}</p>
              )}
            </div>

            <div className="mt-10 space-y-3">
              <div className="flex items-center gap-3 text-sm text-neutral-600">
                <Truck className="w-4 h-4" strokeWidth={1.5} />
                Free shipping on orders over $300
              </div>
              <div className="flex items-center gap-3 text-sm text-neutral-600">
                <RotateCcw className="w-4 h-4" strokeWidth={1.5} />
                Free returns within 30 days
              </div>
            </div>
          </div>
        </div>

        {/* From Every Angle — only when product actually has dedicated angle images */}
        {angleImages.length > 0 && (
          <section className="mt-32" data-testid="from-every-angle">
            <div className="text-center mb-12">
              <p className="text-[11px] tracking-[0.22em] uppercase text-neutral-500 mb-4">
                In Detail
              </p>
              <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tight">
                From Every Angle
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-7">
              {angleImages.map((img, i) => (
                <div
                  key={img}
                  className="aspect-[4/5] overflow-hidden bg-neutral-100 cursor-zoom-in"
                  onClick={() => setLightboxSrc(img)}
                  data-testid={`angle-${i}`}
                >
                  <img
                    src={img}
                    alt={`${product.name} angle ${i + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Related */}
        {relatedList.length > 0 && (
          <section className="mt-32">
            <div className="text-center mb-12">
              <p className="text-[11px] tracking-[0.22em] uppercase text-neutral-500 mb-4">
                You May Also Like
              </p>
              <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tight">
                Complete the Look
              </h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-10">
              {relatedList.map((p) => (
                <Link key={p.id} to={`/product/${p.id}`} className="block">
                  <div className="aspect-[3/4] overflow-hidden bg-neutral-100 mb-4">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                  </div>
                  <p className="text-sm font-medium">{p.name}</p>
                  <p className="text-sm text-neutral-700 mt-1">${p.price}</p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      <Lightbox
        src={lightboxSrc}
        alt={product.name}
        open={Boolean(lightboxSrc)}
        onClose={() => setLightboxSrc(null)}
      />
    </div>
  );
};

export default Product;
